import { z } from "zod";

import type { ClientServiceProfileFieldsFragment } from "../../__generated__/graphql";

export const PET_SERVICE_ID = "21";
export const PET_MAX_PER_TYPE = 3;

export const petConditionOptions = [
  { value: "senior-pet", label: "Senior pet (over 10 years old)" },
  { value: "not-fully-vaccinated", label: "Not fully vaccinated or vaccination status unknown" },
  { value: "has-existing-health-conditions", label: "Has existing health conditions" },
  { value: "pregnant", label: "Pregnant" },
  { value: "aggressive-or-highly-anxious", label: "Aggressive or highly anxious" },
] as const;

export const petConditionKeys = petConditionOptions.map((o) => o.value);

export const petProfileSchema = z.object({
  name: z.string().min(1, "Name is required"),
  breed: z.string().min(1, "Breed is required"),
  age: z
    .string()
    .min(1, "Age is required")
    .refine((val) => /^\d.*[a-zA-Z]$/.test(val), {
      message: "Age must be a number and include a unit (e.g., 2 yrs)",
    }),
  gender: z.enum(["male", "female"], "Gender is required"),
  type: z.enum(["dog", "cat"], "Type is invalid").optional().default("cat"),
  special_conditions: z.array(z.object({ value: z.string(), selected: z.boolean() })).default([]),
  primary: z.boolean().default(true),
});

export type PetProfileFormValues = z.input<typeof petProfileSchema>;

export const emptyPetValues: PetProfileFormValues = {
  name: "",
  breed: "",
  age: "",
  gender: "male",
  type: "cat",
  special_conditions: petConditionOptions.map((o) => ({ value: o.value, selected: false })),
  primary: true,
};

/** Form values -> profileAttributes JSON for the mutation (name excluded, conditions -> string[]). */
export function toPetAttributes(values: PetProfileFormValues) {
  const { name: _name, primary: _primary, special_conditions, ...rest } = values;
  return {
    ...rest,
    special_conditions: (special_conditions ?? []).filter((c) => c.selected).map((c) => c.value),
  };
}

/** Stored profile -> form values (conditions string[] -> checkbox array; name from profile). */
export function fromPetProfile(profile: ClientServiceProfileFieldsFragment): PetProfileFormValues {
  const attrs = (profile.profileAttributes ?? {}) as Record<string, unknown>;
  const stored = (attrs["special_conditions"] as string[] | undefined) ?? [];
  return {
    name: profile.name,
    breed: (attrs["breed"] as string) ?? "",
    age: (attrs["age"] as string) ?? "",
    gender: (attrs["gender"] as "male" | "female") ?? "male",
    type: (attrs["type"] as "dog" | "cat") ?? "cat",
    special_conditions: petConditionOptions.map((o) => ({
      value: o.value,
      selected: stored.includes(o.value),
    })),
    primary: profile.primary,
  };
}

export function hasSelectedPetCondition(conditions?: PetProfileFormValues["special_conditions"]) {
  return (conditions ?? []).some((c) => c.selected);
}

type PetCountProfile = Pick<ClientServiceProfileFieldsFragment, "id" | "profileAttributes">;

/** Max 3 dogs / 3 cats. Returns an error message or null. */
export function validatePetProfile(
  values: PetProfileFormValues,
  profiles: PetCountProfile[],
  isEdit: boolean,
  currentProfileId?: string,
): string | null {
  const newType = values.type;
  if (!newType) return "Pet type is required";

  let dogCount = 0;
  let catCount = 0;
  for (const p of profiles) {
    if (isEdit && currentProfileId && p.id === currentProfileId) continue;
    const t = (p.profileAttributes as Record<string, unknown> | null)?.["type"];
    if (t === "dog") dogCount++;
    else if (t === "cat") catCount++;
  }

  if (newType === "dog" && dogCount >= PET_MAX_PER_TYPE) return "You can have up to 3 dog profiles";
  if (newType === "cat" && catCount >= PET_MAX_PER_TYPE) return "You can have up to 3 cat profiles";
  return null;
}

export const DEFAULT_PET_CARE_WHATSAPP_MESSAGE =
  "Hi, I would like to chat about your Pet Care on your page https://app.luce.sg";
